import { format, formatISO9075, parse, parseISO } from "date-fns";

const jsFormat = "yyyy-MM-dd'T'HH:mm";
const sqlFormat = "yyyy-MM-dd HH:mm:ss";
const sqlDateFormat = "yyyy-MM-dd";
const localeDateFormat = "dd/MM/yyyy HH:mm";

/**
 * Convert JS time string to SQL time string
 * For example, from "2020-01-01T12:22" to "2020-01-01 12:22:00"
 * @param jsString
 */
export const convertJsToSql = (jsString: string) =>
  format(parse(jsString, jsFormat, new Date()), sqlFormat);

/**
 * Convert SQL time string to format used by TextInput
 * For example, from "2020-01-01 12:22:00" to "2020-01-01T12:22"
 * @param sqlString
 */
export const convertSqlToJs = (sqlString: string | null) => {
  if (!sqlString) {
    return format(new Date(), jsFormat);
  } else {
    return format(parse(sqlString, sqlFormat, new Date()), jsFormat);
  }
};

/**
 * Convert SQL date string to Locale date format
 * @param sqlString
 */
export const convertSqlDateToLocale = (sqlString: string) =>
  format(parse(sqlString, sqlDateFormat, new Date()), localeDateFormat);

export const convertIsoToLocale = (isoString: string | null) => {
  if (isoString) {
    return format(parseISO(isoString), localeDateFormat);
  }
};

/**
 * A helper function to convert Javascript Date to mySQL Date.
 * E.g. mysql date: 2020-01-31.
 *
 * @param date date to be converted
 * @returns
 */
export const convertJsDateToMySqlDate = (date: Date) =>
  formatISO9075(date, { representation: "date" });

/**
 * Convert ISO date string to "date month year" and "hour minute"
 * @param isoString
 * @returns formatted date and time
 */
export const convertIsoToDateTime = (isoString: string) => {
  const date = parseISO(isoString);
  
  const formattedDate = format(date, 'dd MMMM yyyy'); // e.g. 22 october 2024
  const formattedTime = format(date, 'HH:mm'); // e.g. 01:34
  
  return {
    date: formattedDate,
    time: formattedTime,
  };
};

/**
 * Convert database datetime string to HTML datetime-local input format
 * WITHOUT timezone conversion - treats the database time as literal values
 * @param sqlString database datetime string (e.g., "2023-12-25 14:30:00")
 * @returns HTML datetime-local format (e.g., "2023-12-25T14:30")
 */
export const convertSqlToJsIgnoreTimezone = (sqlString: string | null) => {
  if (!sqlString) return "";

  // If the string contains 'T' it's probably ISO format: e.g. 2023-12-25T11:00:00Z or with offset
  if (sqlString.includes("T")) {
    // split date and time
    const [datePart, timePartRaw] = sqlString.split("T");
    if (!timePartRaw) return "";
    // remove timezone offset or trailing Z
    const timePartNoOffset = timePartRaw.replace(/([+-]\d{2}:?\d{2}|Z)$/, "");
    // remove seconds if present
    const timeNoSeconds = timePartNoOffset.replace(/:\d{2}(?:\.\d+)?$/, "");
    // remove any trailing non-time characters (e.g. spaces)
    const timeClean = timeNoSeconds.trim();
    return `${datePart}T${timeClean}`;
  }

  // Otherwise assume SQL style 'YYYY-MM-DD HH:mm[:ss]'
  // Remove seconds if present and replace space with 'T'
  return sqlString.replace(/:\d{2}$/, "").replace(" ", "T");
};

/**
 * Convert HTML datetime-local input format to database datetime string
 * WITHOUT timezone conversion - treats input as literal values
 * @param jsString HTML datetime-local format (e.g., "2023-12-25T14:30")
 * @returns database datetime format (e.g., "2023-12-25 14:30:00")
 */

export const parseIgnoreTimezone = (value: string) => {
  if (!value) return null;

  // Expect: "2025-12-22T12:00"
  return value.replace("T", " ") + ":00";
};



export const formatIgnoreTimezone = (value: string) => {
  if (!value) return "";

  // Expect: "2025-12-22 12:00:00"
  const [date, time] = value.split(" ");

  if (!date || !time) return "";

  const [hh, mm] = time.split(":");

  // Return format wajib untuk datetime-local
  return `${date}T${hh}:${mm}`;
};


